import { isNullable } from '@qtek/shared/utils';
import { EMPTY } from 'rxjs';
import { QtSearchDirective } from '../directives/search.directive';
import { SearchPredicate } from '../models/search';
import { transformerFactory } from './transform';

// New helper to safely convert any value to string
function safeToString(value: unknown): string {
  if (isNullable(value)) {
    return '';
  }

  try {
    // Handle objects separately to catch circular-reference errors
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  } catch {
    // Fallbacks if JSON.stringify or String conversion throw
    try {
      return String(value);
    } catch {
      return '';
    }
  }
}

/**
 * Default implementation of {@link SearchPredicate}.
 * Check if query can be found in given data.
 *
 * @param data Row of data.
 * @param query Query to find in given row.
 * @param columns List of column's id to search.
 */
export const defaultSearchPredicate: SearchPredicate = <T>(
  data: T,
  query: string,
  columns: string[],
  valueAccessor: (row: T, path: string) => any
) => {
  // Escape regex special characters
  const normalizedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const r = new RegExp(normalizedQuery, 'gi');

  return columns.some(column => {
    const value = valueAccessor(data, column);
    if (isNullable(value)) {
      return false;
    }

    const valueString = safeToString(value);

    return r.test(valueString);
  });
};

/**
 * Filter data based on query.
 *
 * @param data Array to filter.
 * @param search Instance of {@link QtSearchDirective}.
 * @param searchPredicate Function which determines if row should be included or not.
 */
export function searchData<T>(
  data: T[],
  search: QtSearchDirective,
  searchPredicate: SearchPredicate,
  valueAccessor: (row: T, path: string) => any
): T[] {
  if (!search || !search.query) {
    return data;
  }

  return data.filter(row =>
    searchPredicate(row, search.query, search.columns, valueAccessor)
  );
}

/**
 * Search data be configuration from {@link QtSearchDirective}.
 *
 * @param search Instance of {@link QtSearchDirective} to get events from.
 * @param searchPredicate Function which determines if row should be included or not.
 */
export function search<T>(
  search: QtSearchDirective,
  searchPredicate: SearchPredicate,
  valueAccessor: (row: T, path: string) => any
) {
  return transformerFactory<T, string>(
    search ? search.searchChange : EMPTY,
    ([data]) => searchData<T>(data, search, searchPredicate, valueAccessor)
  );
}
